import React from 'react';
import { Document, View, Text, StyleSheet } from '@react-pdf/renderer';
import type { Grade } from '../../../types';
import type { StudentReportData } from '../../../services/analyticsService';
import { SEVERITY_PDF_TONE } from '../../../utils/severityTone';
import { attendanceRateColor } from '../../../utils/attendanceStatus';
import {
  Page, DataTable, Pill, StatStrip,
  pdfColors, pdfLetter, pdfSpacing, pdfStatusTones, fmtClassName, fmtDate, fmtMoney,
  type DataTableColumn, type SchoolBranding,
} from '../../../utils/pdf';
import { MARK_GREEN, MARK_RED, ScoreBar, gradeColorOf } from '../../examination/pdf/scoreMarks';

interface Props {
  report: StudentReportData;
  grades: Grade[];
  currency: string;
  branding: SchoolBranding;
  generatedAt: string;
}

type Data = StudentReportData;
type Exam = { examName: string; month: number; year: number; overallPercentage: number | null; subjects: unknown[] };
type Subject = Data['academicData']['academicPerformance']['subjectWisePerformance'][number];
type Letter = Data['administrativeData']['lettersIssued'][number];

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/** Side by side while both tables are short; stacked once either runs long, so no row is split across pages. */
const SIDE_BY_SIDE_ROWS = 14;
/** The most recent letters shown; the rest are counted. */
const LETTERS_SHOWN = 12;


const label = { fontSize: 6.8, fontWeight: 700, textTransform: 'uppercase', letterSpacing: pdfLetter.openStrong, color: pdfColors.textMuted } as const;

const styles = StyleSheet.create({
  section: { marginTop: pdfSpacing.md },
  head: {
    flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
    paddingBottom: 4, marginBottom: 8, borderBottomWidth: 0.8, borderBottomColor: pdfColors.dividerStrong, borderBottomStyle: 'solid',
  },
  title: { fontSize: 8.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: pdfLetter.openStrong, color: pdfColors.textSecondary },
  badge: { borderRadius: 999, paddingVertical: 2.5, paddingHorizontal: 9 },
  badgeText: { fontSize: 7.5, fontWeight: 700, color: '#ffffff' },
  twoCol: { flexDirection: 'row' },
  col: { flex: 1 },
  colGap: { width: 14 },
  stackGap: { height: 10 },
  strong: { fontWeight: 600, color: pdfColors.textPrimary },
  best: { color: MARK_GREEN, fontWeight: 600 },
  low: { color: MARK_RED, fontWeight: 600 },
  chips: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', marginTop: 7 },
  chipsLabel: { ...label, marginRight: 6 },
  chip: { marginRight: 4, marginBottom: 3 },
  attendance: { flexDirection: 'row', alignItems: 'center' },
  rateBox: { width: 104, alignItems: 'center' },
  rate: { fontSize: 24, fontWeight: 700 },
  rateLabel: { ...label, marginTop: 2 },
  // Up to five figures share one row evenly.
  tiles: { flex: 1, flexDirection: 'row' },
  tile: { flex: 1, maxWidth: 92, marginRight: 6, paddingVertical: 6, paddingHorizontal: 6, borderRadius: 4, backgroundColor: pdfColors.neutralSoft, alignItems: 'center' },
  tileValue: { fontSize: 12, fontWeight: 700 },
  tileLabel: { ...label, fontSize: 6, marginTop: 1 },
  tileDetail: { fontSize: 6, color: pdfColors.textFaint, marginTop: 1, textAlign: 'center' },
  letter: {
    flexDirection: 'row', alignItems: 'center', paddingVertical: 5, paddingHorizontal: 8, marginBottom: 3,
    borderRadius: 3, backgroundColor: pdfColors.surfaceSoft, borderLeftWidth: 2.5, borderLeftStyle: 'solid',
  },
  letterDate: { width: 60, fontSize: 7.5, color: pdfColors.textSecondary },
  letterCat: { width: 92, fontSize: 6.8, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.3 },
  letterTitle: { flex: 1, fontSize: 8, color: pdfColors.textPrimary, paddingRight: 8 },
  letterAmount: { fontSize: 8, fontWeight: 700, textAlign: 'right' },
  letterPaid: { fontSize: 6.5, fontWeight: 500, color: pdfColors.textMuted },
  more: { textAlign: 'center', fontSize: 6.8, color: pdfColors.textFaint, marginTop: 2, marginBottom: 6 },
  totals: { marginTop: 6 },
  empty: {
    marginTop: pdfSpacing.lg, paddingVertical: 36, textAlign: 'center', fontSize: 9, color: pdfColors.textMuted, fontStyle: 'italic',
    borderWidth: 0.6, borderColor: pdfColors.dividerStrong, borderStyle: 'dashed', borderRadius: 4,
  },
});

/** A section's heading, with its headline figure on the right. */
const Head: React.FC<{ title: string; badge?: { text: string; color: string } }> = ({ title, badge }) => (
  <View style={styles.head} wrap={false} minPresenceAhead={60}>
    <Text style={styles.title}>{title}</Text>
    {badge && <View style={[styles.badge, { backgroundColor: badge.color }]}><Text style={styles.badgeText}>{badge.text}</Text></View>}
  </View>
);

/**
 * A student's report for a session, as the Student Reports screen shows it:
 * the school's letterhead; how they did — every exam, every subject's average,
 * best and lowest, where they are strong and where they need work; their
 * attendance; and the letters and fines they have been issued. A section
 * with nothing to show is left out.
 */
const StudentReportDocument: React.FC<Props> = ({ report, grades, currency, branding, generatedAt }) => {
  const gradeColor = gradeColorOf(grades);
  const { student, academicData: ac, administrativeData: ad } = report;
  const perf = ac.academicPerformance;
  const enrolment = student.enrollmentInfo;
  // The server lists exams latest first; only those with a score count.
  const exams = (ac.resultsHistory as Exam[]).filter(e => e.overallPercentage != null);
  const subjects = perf.subjectWisePerformance;
  const att = ad.attendanceOverview;
  const letters = ad.lettersIssued;
  const sum = ad.lettersSummary;
  const money = (n: number) => fmtMoney(n, currency);

  const examColumns: Array<DataTableColumn<Exam>> = [
    { key: 'exam', header: 'Exam', flex: 1, render: e => <Text style={styles.strong}>{e.examName}</Text> },
    { key: 'when', header: 'When', width: 52, render: e => `${MONTHS[e.month - 1] ?? ''} ${e.year}` },
    { key: 'score', header: 'Score', width: 72, render: e => <ScoreBar pct={e.overallPercentage ?? 0} grades={grades} /> },
    { key: 'papers', header: 'Papers', width: 36, align: 'center', render: e => String(e.subjects.length) },
  ];
  const subjectColumns: Array<DataTableColumn<Subject>> = [
    { key: 'subject', header: 'Subject', flex: 1, render: s => <Text style={styles.strong}>{s.subjectName}</Text> },
    { key: 'avg', header: 'Average', width: 72, render: s => <ScoreBar pct={s.averagePercentage} grades={grades} /> },
    { key: 'best', header: 'Best', width: 38, align: 'right', render: s => <Text style={styles.best}>{s.bestPerformance.toFixed(1)}%</Text> },
    { key: 'low', header: 'Low', width: 38, align: 'right', render: s => <Text style={styles.low}>{s.worstPerformance.toFixed(1)}%</Text> },
    { key: 'tests', header: 'Tests', width: 32, align: 'center', render: s => String(s.totalTests) },
  ];
  const sideBySide = exams.length > 0 && subjects.length > 0 && exams.length <= SIDE_BY_SIDE_ROWS && subjects.length <= SIDE_BY_SIDE_ROWS;
  const examTable = exams.length > 0 && <DataTable columns={examColumns} rows={exams} compact small />;
  const subjectTable = subjects.length > 0 && <DataTable columns={subjectColumns} rows={subjects} compact small />;

  const chipRow = (caption: string, names: string[], tone: 'success' | 'warning') => names.length > 0 && (
    <View style={styles.chips} wrap={false}>
      <Text style={styles.chipsLabel}>{caption}</Text>
      {names.map(n => <View key={n} style={styles.chip}><Pill dense label={n} tone={tone} /></View>)}
    </View>
  );

  const tile = (value: number, text: string, color: string, detail?: { informed: number; uninformed: number }) => value > 0 && (
    <View key={text} style={styles.tile}>
      <Text style={[styles.tileValue, { color }]}>{value}</Text>
      <Text style={styles.tileLabel}>{text}</Text>
      {detail && <Text style={styles.tileDetail}>{detail.informed} informed{'\n'}{detail.uninformed} uninformed</Text>}
    </View>
  );

  const letterRow = (l: Letter) => {
    const tone = pdfStatusTones[SEVERITY_PDF_TONE[l.category.severity] ?? 'info'];
    const settled = l.paymentStatus === 'paid' || l.paymentStatus === 'waived';
    return (
      <View key={l._id} style={[styles.letter, { borderLeftColor: tone.fg }]} wrap={false}>
        <Text style={styles.letterDate}>{fmtDate(l.issuedAt)}</Text>
        <Text style={[styles.letterCat, { color: tone.fg }]}>{l.category.label}</Text>
        <Text style={styles.letterTitle}>{l.title || l.reason || ''}</Text>
        {l.amount
          ? (
            <Text style={[styles.letterAmount, { color: settled ? MARK_GREEN : MARK_RED }]}>
              {money(l.amount)}
              {l.paidAmount ? <Text style={styles.letterPaid}>{`  paid ${money(l.paidAmount)}`}</Text> : null}
            </Text>
          )
          : <Text style={[styles.letterAmount, { color: pdfColors.textFaint }]}>—</Text>}
      </View>
    );
  };

  const hasAcademics = exams.length > 0 || subjects.length > 0;
  const hasAttendance = att.totalDays > 0;
  const hasMoney = sum.outstandingAmount + sum.collectedAmount + sum.waivedAmount > 0;
  const cls = enrolment?.className ? fmtClassName(enrolment.className) : null;
  const roll = enrolment?.rollNumber ?? student.rollNumber;

  return (
    <Document title={`Student Report — ${student.name}`} author={branding.name}>
      <Page
        branding={branding}
        meta={{ title: `Student Report — ${student.name}`, subtitle: [cls, enrolment?.sessionName].filter(Boolean).join('  ·  '), generatedAt }}
        official={{ details: [
          ...(roll != null && roll !== '' ? [{ label: 'Roll number', value: String(roll) }] : []),
          ...(cls ? [{ label: 'Class', value: enrolment?.sectionName ? `${cls} · ${enrolment.sectionName}` : cls }] : []),
          ...(enrolment?.sessionName ? [{ label: 'Session', value: enrolment.sessionName }] : []),
        ] }}
      >
        {hasAcademics && (
          <View style={styles.section}>
            <Head
              title="Academic performance"
              badge={exams.length > 0 ? { text: `Overall ${perf.overallPercentage.toFixed(1)}%`, color: gradeColor(perf.overallPercentage) } : undefined}
            />
            {sideBySide ? (
              <View style={styles.twoCol} wrap={false}>
                <View style={styles.col}>{examTable}</View>
                <View style={styles.colGap} />
                <View style={styles.col}>{subjectTable}</View>
              </View>
            ) : (
              <>
                {examTable}
                {examTable && subjectTable && <View style={styles.stackGap} />}
                {subjectTable}
              </>
            )}
            {chipRow('Strong areas', perf.strengths, 'success')}
            {chipRow('Needs work', perf.improvements, 'warning')}
          </View>
        )}

        {hasAttendance && (
          <View style={styles.section} wrap={false}>
            <Head title="Attendance" badge={{ text: `${att.attendanceRate}%`, color: attendanceRateColor(att.attendanceRate) }} />
            <View style={styles.attendance}>
              <View style={styles.rateBox}>
                <Text style={[styles.rate, { color: attendanceRateColor(att.attendanceRate) }]}>{att.attendanceRate}%</Text>
                <Text style={styles.rateLabel}>Attendance rate</Text>
              </View>
              <View style={styles.tiles}>
                {tile(att.totalDays, 'School days', pdfColors.textPrimary)}
                {tile(att.present, 'Present', MARK_GREEN)}
                {tile(att.late.total, 'Late', '#d97706', att.late)}
                {tile(att.halfDay.total, 'Half day', '#7c3aed', att.halfDay)}
                {tile(att.absent.total, 'Absent', MARK_RED, att.absent)}
              </View>
            </View>
          </View>
        )}

        {letters.length > 0 && (
          <View style={styles.section}>
            <Head title="Letters & fines" badge={{ text: `${letters.length} issued`, color: pdfColors.textMuted }} />
            {letters.slice(0, LETTERS_SHOWN).map(letterRow)}
            {letters.length > LETTERS_SHOWN && <Text style={styles.more}>The latest {LETTERS_SHOWN} of {letters.length} are shown.</Text>}
            {hasMoney && (
              <View style={styles.totals}>
                <StatStrip
                  dense
                  items={[
                    { label: 'Outstanding', value: money(sum.outstandingAmount), tone: 'danger' },
                    { label: 'Collected', value: money(sum.collectedAmount), tone: 'success' },
                    { label: 'Waived', value: money(sum.waivedAmount), tone: 'neutral' },
                  ]}
                />
              </View>
            )}
          </View>
        )}

        {!hasAcademics && !hasAttendance && letters.length === 0 && (
          <Text style={styles.empty}>No exam results, attendance or letters are on record for this student yet.</Text>
        )}
      </Page>
    </Document>
  );
};

export default StudentReportDocument;
